/**
 * Utilities for flattening/unflattening nested objects to/from dot-notation
 * key–value pairs, and for deep-merging a patch onto a base object.
 *
 * Used by the ContentOverrides system (src/contexts/ContentOverridesContext)
 * so that Supabase rows of the form { content_key: "hero.title", value: "…" }
 * can be unflattened back into the nested structure expected by the CONTENT
 * type in src/i18n/content.ts, and then deep-merged on top of the static
 * copy so every useI18n() consumer transparently gets admin-set overrides.
 */

/**
 * Flattens a nested object to a flat Record<string, string>.
 * Only string leaves are included; arrays and other non-object non-string
 * values are skipped (the i18n content tree only has string leaves).
 *
 * flattenObject({ hero: { title: "Foo" } }) → { "hero.title": "Foo" }
 */
export function flattenObject(
  obj: unknown,
  prefix = ""
): Record<string, string> {
  const result: Record<string, string> = {};
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return result;
  }
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") {
      result[key] = v;
    } else if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      Object.assign(result, flattenObject(v, key));
    }
    // Arrays (e.g. feature lists) are intentionally skipped — they're not
    // individually addressable as override keys in the current design.
  }
  return result;
}

/**
 * Unflattens a flat dot-notation Record<string, string> back into a nested
 * object.
 *
 * unflattenObject({ "hero.title": "Foo" }) → { hero: { title: "Foo" } }
 */
export function unflattenObject(
  flat: Record<string, string>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(".");
    let cur: Record<string, unknown> = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) {
        cur[parts[i]] = {};
      }
      cur = cur[parts[i]] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return result;
}

/**
 * Deep-merges `patch` onto `base`, returning a new object that has all of
 * `base`'s keys with string leaves overridden by matching `patch` keys.
 * Non-matching keys from `base` are preserved unchanged.
 *
 * Only string leaves are replaced (matching the i18n content shape). The
 * merge is purely additive from `patch`'s perspective — it never removes
 * keys that exist in `base` but not in `patch`.
 */
export function deepMerge<T>(base: T, patch: Record<string, unknown>): T {
  if (typeof base !== "object" || base === null) return base;
  const result = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null) continue;
    if (
      typeof v === "object" &&
      !Array.isArray(v) &&
      typeof result[k] === "object" &&
      result[k] !== null
    ) {
      result[k] = deepMerge(result[k], v as Record<string, unknown>);
    } else if (typeof v === "string") {
      result[k] = v;
    }
    // Skip non-string, non-object patch values (arrays, numbers, booleans)
    // — the i18n content tree only has string leaves.
  }
  return result as T;
}
